import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { StaffQuotationService, StaffQuotationSummary } from '../services/staff-quotation.service';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { LoaderService } from '../services/loader.service';
import {
    computeQuotationKpis,
    defaultQuotationDateRange,
    matchesQuotationDateRange,
    matchesQuotationFilter,
    matchesQuotationSearch,
    QuotationFilter,
    QuotationKpiStats
} from './quotation.utils';
import {
    QuotationKpiBarComponent,
    QuotationTableComponent
} from './components/quotation-shared.components';

@Component({
    selector: 'app-quotation-review',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ButtonModule,
        SelectModule,
        InputTextModule,
        ToastModule,
        ToolbarModule,
        QuotationKpiBarComponent,
        QuotationTableComponent
    ],
    providers: [MessageService],
    templateUrl: './quotation-review.component.html',
    styleUrl: './quotation-review.component.scss'
})
export class QuotationReviewComponent implements OnInit {
    allQuotations: StaffQuotationSummary[] = [];
    filteredQuotations: StaffQuotationSummary[] = [];
    kpiStats: QuotationKpiStats = {
        total: 0,
        submitted: 0,
        underReview: 0,
        punched: 0,
        rejected: 0
    };

    searchQuery = '';
    selectedStatus: QuotationFilter = 'ALL';
    fromDate = '';
    toDate = '';

    readonly statusOptions = [
        { label: 'All', value: 'ALL' as const },
        { label: 'Submitted', value: 'SUBMITTED' as const },
        { label: 'Under review', value: 'UNDER_REVIEW' as const },
        { label: 'Punched', value: 'PUNCHED' as const },
        { label: 'Rejected', value: 'REJECTED' as const }
    ];

    private scope = { companyId: '', divisionId: '', whsId: '' };

    constructor(
        private readonly quotationService: StaffQuotationService,
        private readonly inMemoryStorage: InMemoryStorageService,
        private readonly loaderService: LoaderService,
        private readonly messageService: MessageService,
        private readonly router: Router
    ) {}

    ngOnInit(): void {
        const range = defaultQuotationDateRange();
        this.fromDate = range.fromDate;
        this.toDate = range.toDate;
        this.scope = {
            companyId: this.inMemoryStorage.get('company') || '',
            divisionId: this.inMemoryStorage.get('division') || '',
            whsId:
                this.inMemoryStorage.get('warehouseId') ||
                this.inMemoryStorage.get('whsId') ||
                this.inMemoryStorage.get('warehouse') ||
                ''
        };
        this.reload();
    }

    reload(): void {
        if (!this.scope.companyId || !this.scope.divisionId || !this.scope.whsId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Scope missing',
                detail: 'Company, division and warehouse are required.'
            });
            return;
        }
        this.loaderService.show('Loading draft orders...', 'Please wait...');
        this.quotationService
            .listQuotations(this.scope, 'ALL')
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: (rows) => {
                    this.allQuotations = rows || [];
                    this.applyFilters();
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Failed to load draft orders',
                        detail: err?.error?.message || err?.error || 'Please retry.'
                    });
                }
            });
    }

    onKpiFilterSelect(filter: string): void {
        this.selectedStatus = filter as QuotationFilter;
        this.applyFilters();
    }

    onFilterChange(): void {
        this.applyFilters();
    }

    onSearchChange(): void {
        this.applyFilters();
    }

    onDateChange(): void {
        this.applyFilters();
    }

    openQuotation(quotationNumber: string): void {
        this.router.navigate(['/quotation-review', quotationNumber]);
    }

    private applyFilters(): void {
        this.kpiStats = computeQuotationKpis(this.allQuotations);
        this.filteredQuotations = this.allQuotations.filter(
            (row) =>
                matchesQuotationFilter(row.status, this.selectedStatus) &&
                matchesQuotationSearch(row, this.searchQuery) &&
                matchesQuotationDateRange(row.createdAt, this.fromDate, this.toDate)
        );
    }
}
